import React, { useState } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { Text } from './text';
import { Button } from './button';
import { createPortalSession } from '../api/billing';

// Helper to format date
const formatDate = (timestamp) => {
  if (!timestamp) return '-';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

// Status badge configurations
const STATUS_CONFIG = {
  trialing: {
    label: 'Prueba',
    bgColor: 'bg-blue-100',
    textColor: 'text-blue-800',
  },
  active: {
    label: 'Activo',
    bgColor: 'bg-green-100',
    textColor: 'text-green-800',
  },
  past_due: {
    label: 'Pago pendiente',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
  },
  canceled: {
    label: 'Cancelado',
    bgColor: 'bg-gray-100',
    textColor: 'text-gray-800',
  },
  unpaid: {
    label: 'Sin pagar',
    bgColor: 'bg-red-100',
    textColor: 'text-red-800',
  },
};

/**
 * SubscriptionStatus - Displays subscription status with usage bar
 *
 * @param {Object} props
 * @param {Object} props.orgData - Organization data with subscription fields
 * @param {Object} props.usage - Usage data { pages_processed }
 * @param {boolean} props.usageLoading - Whether usage is loading
 * @param {Object} props.plan - Plan data with page_limit
 */
const SubscriptionStatus = ({ orgData, usage, usageLoading, plan }) => {
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState('');

  const subscriptionStatus = orgData?.subscriptionStatus;
  const cancelAtPeriodEnd = orgData?.cancelAtPeriodEnd;
  const currentPeriodEnd = orgData?.currentPeriodEnd;
  const trialEndsAt = orgData?.trialEndsAt;

  const statusConfig = STATUS_CONFIG[subscriptionStatus] || STATUS_CONFIG.canceled;

  // Calculate usage percentage
  const pageLimit = plan?.page_limit || 0;
  const pagesUsed = usage?.total_pages || 0;
  const usagePercent = pageLimit > 0 ? Math.min((pagesUsed / pageLimit) * 100, 100) : 0;
  console.log("Usage percent:", usagePercent);
  console.log("Pages used:", pagesUsed);
  console.log("Page limit:", orgData);
  console.log("Plan:", plan);
  console.log("Usage:", usage);
  // Determine progress bar color
  const getProgressColor = () => {
    if (usagePercent >= 90) return 'bg-red-500';
    if (usagePercent >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const handleOpenPortal = async () => {
    if (!orgData?.id) return;

    setError('');
    setPortalLoading(true);
    try {
      const { portalUrl } = await createPortalSession(orgData.id);
      window.location.href = portalUrl;
    } catch (err) {
      setError(err.message || 'Error al abrir el portal de pagos');
      setPortalLoading(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Status Badge and Date */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
            {statusConfig.label}
          </span>
          {cancelAtPeriodEnd && subscriptionStatus === 'active' && (
            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
              Se cancelará
            </span>
          )}
        </div>
        <div className="text-right">
          {subscriptionStatus === 'trialing' && trialEndsAt && (
            <Text className="text-sm text-azul-surelia/70">
              Prueba termina: {formatDate(trialEndsAt)}
            </Text>
          )}
          {subscriptionStatus === 'active' && !cancelAtPeriodEnd && currentPeriodEnd && (
            <Text className="text-sm text-azul-surelia/70">
              Próximo cobro: {formatDate(currentPeriodEnd)}
            </Text>
          )}
          {subscriptionStatus === 'active' && cancelAtPeriodEnd && currentPeriodEnd && (
            <Text className="text-sm text-amber-700">
              Acceso hasta: {formatDate(currentPeriodEnd)}
            </Text>
          )}
        </div>
      </div>

      {/* Usage Progress Bar */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Text className="text-sm font-medium text-azul-surelia/70">
            Páginas procesadas este mes
          </Text>
          {usageLoading ? (
            <Text className="text-sm text-azul-surelia/50">Cargando...</Text>
          ) : (
            <Text className="text-sm font-medium">
              {pagesUsed.toLocaleString()} / {pageLimit.toLocaleString()}
            </Text>
          )}
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2.5">
          <div
            className={`h-2.5 rounded-full transition-all duration-300 ${getProgressColor()}`}
            style={{ width: `${usagePercent}%` }}
          />
        </div>
        {usagePercent >= 90 && !usageLoading && (
          <Text className="text-xs text-red-600">
            Has utilizado más del 90% de tu límite mensual
          </Text>
        )}
      </div>

      {/* Pending Cancellation Warning */}
      {cancelAtPeriodEnd && subscriptionStatus === 'active' && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
          <div className="flex items-start gap-3">
            <ExclamationTriangleIcon className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <Text className="text-sm font-medium text-amber-800">
                Tu suscripción se cancelará el {formatDate(currentPeriodEnd)}
              </Text>
              <Text className="text-sm text-amber-700 mt-1">
                Tendrás acceso a todas las funciones hasta esa fecha.
                Para reactivar tu suscripción, haz clic en "Administrar suscripción".
              </Text>
            </div>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
          <Text className="text-sm text-red-700">{error}</Text>
        </div>
      )}

      {/* Manage Subscription Button */}
      <Button
        onClick={handleOpenPortal}
        disabled={portalLoading}
        outline
        className="w-full"
      >
        {portalLoading ? 'Abriendo portal...' : 'Administrar suscripción'}
      </Button>
    </div>
  );
};

export default SubscriptionStatus;
